import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSwitch } from '../../reducers/switch';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import FilterIcon from '../../assets/icons/filter-icon.svg?react';
import DownloadIcon from '../../assets/icons/download-icon.svg?react';
import SearchInput from '../../components/SearchInput';
import Select from '../../components/Select';
import { useNavigate } from 'react-router';
import CalenderIcon from '../../assets/icons/calender-icon.svg?react';
import OutlineEyeIcon from '../../assets/icons/outline-eye-icon.svg?react';
import RefreshIcon from '../../assets/icons/refresh-icon.svg?react';
import Pagination from '../../components/CustomPagination';
import {
  GetSubmmissionDetailsResponse,
  OshaReportingApiOshaReportingControllerGetOshaSubmissionDetailsRequest,
  OshaReportingControllerGetOshaSubmissionDetailsClassificationEnum,
  OshaReportingControllerGetOshaSubmissionDetailsStatusEnum,
  OshaReportingControllerGetOshaSubmissionDetailsTypeEnum,
  OshaReportingControllerGetOshaSubmissionDetailsTypeOfLossEnum,
} from '@EHSNavigator/backend';
import moment from 'moment';
import { useDebounce } from '@uidotdev/usehooks';
import FilterClearIcon from '../../assets/icons/filter-clear-icon.svg?react';
import NoItemsFound from '../../components/NoItemsFound';
import Loader from '../../components/Loader';
import {
  handleExportToCSV,
  handleOshaReportingLogsExportToExcel,
} from '../../utils/exportTable';
import Button from '../../components/Button';
import { useClickOutside } from '../../utils/clickOutSide';
import { useButtonSizeSmall } from '../../utils/buttonSizes';
import ModalComponent from '../../components/ModalComponent';
import { toggleModal } from '../../reducers/modalToggle';
import OshaSummaryModal from '../../components/OshaSummaryModal';
import { dateOshaValidation } from '../../utils/oshaDateValidation';

interface OshaReportingLogsProps {
  otherClasses?: string;
  params: OshaReportingApiOshaReportingControllerGetOshaSubmissionDetailsRequest;
  departments: {
    label: string;
    value: string;
  }[];
  data: GetSubmmissionDetailsResponse;
  noDataMessage: string;
  isIncidentsLoading: boolean;
  isLogsNotAvailable: boolean;
}

const OshaReportingLogs: React.FC<OshaReportingLogsProps> = ({
  otherClasses,
  params,
  departments,
  data,
  noDataMessage,
  isIncidentsLoading,
  isLogsNotAvailable,
}) => {
  const myReportedIncidentsClasses = clsx(
    otherClasses,
    'flex flex-col w-full flex-1',
  );
  const [toggle, setToggle] = useState(false);
  const [toggleMob, setToggleMob] = useState(false);
  const ref = useRef(null);
  useClickOutside({ ref, setToggle });
  const refMob = useRef(null);
  useClickOutside({ ref: refMob, setToggle: setToggleMob });
  const navigate = useNavigate();
  const [state, setState] = useState({
    year: params?.year,
    search: params?.search || '',
    page: Number(params?.page),
    limit: params?.limit || 10,
    department: params?.departmentId || '',
    status: params?.status || '',
    classification: params?.classification || '',
    typeOfLoss: params?.typeOfLoss || '',
    type: params?.type || '',
  });
  const debouncedSearch = useDebounce(state?.search, 600);
  const dispatch = useDispatch();
  const prevFiltersRef = useRef({});
  const modalName = useSelector(
    (state: { modalToggle: { modalName: string } }) =>
      state.modalToggle.modalName,
  );
  const isFirstRun = useRef(true);

  useEffect(() => {
    const filters = {
      search: debouncedSearch,
      limit: state?.limit,
      department: state?.department,
      status: state?.status,
      year: state.year,
      classification: state?.classification,
      typeOfLoss: state?.typeOfLoss,
      type: state?.type,
    };

    let shouldResetPage = false;
    if (!isFirstRun.current) {
      const filtersChanged =
        JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current);
      if (filtersChanged) {
        shouldResetPage = true;
        setState((prev) => ({ ...prev, page: 1 }));
      }
    }
    const paramsSearch = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'limit' && Number(value) === 10) {
        return;
      }
      if (value) paramsSearch.set(key, value.toString());
    });
    if (state.page !== 1) {
      paramsSearch.set('page', state?.page.toString());
    }
    if (shouldResetPage) {
      paramsSearch.delete('page');
      setState((prev) => ({ ...prev, page: 1 }));
    }
    prevFiltersRef.current = filters;
    if (!isFirstRun.current) {
      navigate(`/osha-reporting/logs?${paramsSearch.toString()}`);
    }

    isFirstRun.current = false;
  }, [
    debouncedSearch,
    state.page,
    state.year,
    state.limit,
    state.department,
    state.status,
    state.classification,
    state.typeOfLoss,
    state.type,
  ]);

  const updateQuery = (queryName: string, queryValue: string) => {
    const params = new URLSearchParams(window.location.search);
    if (queryName === 'page' && queryValue === '1') {
      params.delete('page');
    } else {
      params.set(queryName, queryValue);
    }

    navigate(`/osha-reporting/logs?${params.toString()}`);
  };

  const handlePageChange = (newPage: number) => {
    setState((prev) => ({ ...prev, page: newPage }));
    updateQuery('page', String(newPage));
  };

  const exportData = {
    header: [
      'Sr.',
      'Ref#',
      'Status',
      'Employee Effected',
      'Date',
      'Type Of Loss',
      'Failed Reason',
    ],
    columnWidths: [
      { width: 5 },
      { width: 18 },
      { width: 30 },
      { width: 20 },
      { width: 30 },
      { width: 15 },
      { width: 15 },
    ],
    exportData: data?.cases?.map((incident: any, index: number) => {
      return {
        'Sr.': (state.page - 1) * state.limit + index + 1,
        'Ref#': incident.investigation?.investigationRefId,
        Status: incident.status,
        'Employee Effected': incident?.investigation?.user?.fullName,
        Date: moment(incident.createdAt).format('YYYY-MM-DD'),
        'Type Of Loss': incident?.investigation?.typeOfLoss,
        'Failed Reason':
          incident?.failedReason?.error?.map((node: string) => node) || '---',
      };
    }),
  };

  const options = [
    {
      label: 'Export CSV',
      onClick: () => {
        handleExportToCSV(exportData?.exportData, exportData?.header);
      },
    },
    {
      label: 'Export XLSX',
      onClick: () => {
        handleOshaReportingLogsExportToExcel(
          exportData?.exportData,
          exportData?.header,
          exportData?.columnWidths,
        );
      },
    },
  ];

  const isFiltersActive =
    state?.search !== '' ||
    state?.limit !== 10 ||
    state?.department !== '' ||
    state?.status !== '' ||
    state?.classification !== '' ||
    state?.typeOfLoss !== '' ||
    state?.type !== '';

  const startYear = 2025;
  const currentYear = moment().year();

  const list: { label: string; value: string }[] = [];

  for (let year = startYear; year <= currentYear; year++) {
    list.push({
      label: year.toString(),
      value: year.toString(),
    });
  }

  const lg = useButtonSizeSmall();

  const isDateAvailable = dateOshaValidation();

  return (
    <div
      className={myReportedIncidentsClasses}
      data-testid="osha-reporting-logs"
    >
      {!isLogsNotAvailable && (
        <div className="!mb-5 hidden items-center justify-end gap-2.5 lg:flex">
          <div ref={ref} className="relative flex">
            <Button
              onClick={() => setToggle(!toggle)}
              otherClasses="!p-0 bg-transparent shadow-none !text-brand-byzantine disabled:bg-transparent disabled:!text-neutral-tones-text/50 !text-lg  hover:!bg-transparent hover:!border-transparent"
              disable={data?.cases?.length === 0}
            >
              <DownloadIcon className="rotate-180 [&>g>path]:fill-current" />{' '}
              Export
            </Button>
            {toggle && (
              <ul
                className={clsx(
                  'bg-light-translucent-background ring-neutral-tones-text/35 dark:bg-dark-translucent-background absolute top-10 right-0 z-10 flex min-w-100 flex-col rounded-md p-2.5 ring md:w-full lg:top-11',
                )}
              >
                {options.map((option, index) => (
                  <button
                    key={index}
                    className={clsx(
                      'font-inter-variable text-neutral-tones-text border-b-neutral-tones-text/35 hover:text-brand-byzantine global-transition cursor-pointer border-b py-2 text-left text-sm font-normal whitespace-nowrap first:pt-0 last:border-b-0 last:pb-0 disabled:cursor-not-allowed disabled:text-neutral-600',
                    )}
                    onClick={() => {
                      option.onClick();
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </ul>
            )}
          </div>
          <Button
            otherClasses=""
            onClick={() => dispatch(toggleModal('osha-summary'))}
            size={lg}
            disable={!data?.summary}
          >
            View Summary (OSHA 300A)
          </Button>
        </div>
      )}
      <div className="flex items-start gap-2.5">
        <button
          className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
          onClick={() => dispatch(toggleSwitch())}
        >
          <ToggleIcon />
        </button>
        <div className="w-full">
          <div className="flex flex-wrap items-center justify-between">
            <h1 className="h3 flex items-center gap-2">
              {isLogsNotAvailable ? `OSHA 300A Log` : `OSHA 300 Log`}{' '}
              <span className="bg-brand-fair-orange rounded-10 text-neutral-tones-text-revert h4 hidden px-2.5 py-1.5 lg:block">
                {state?.year}
              </span>
            </h1>
            <div className="bg-brand-fair-orange relative flex items-center gap-2.5 rounded p-1 lg:bg-transparent">
              <CalenderIcon className="lg:[&>g>path]:fill-brand-byzantine h-6 w-6 [&>g>path]:fill-white" />
              <p className="text-neutral-tones-text font-inter-variable hidden items-center text-lg font-medium italic lg:flex">
                Show Logs from
              </p>
              <Select
                filterSelect
                list={[
                  {
                    label: '2024',
                    value: '2024',
                  },
                  ...list,
                ]}
                value={state?.year?.toString() || ''}
                placeholder="Status"
                otherClasses={{
                  container: '!static lg:!relative  w-fit',
                  button:
                    ' !bg-transparent left-0 !ring-0  !p-0 !text-white lg:!text-brand-byzantine !font-bold [&>svg>path]:!fill-current',
                  listContainer:
                    '!top-10 lg:!top-8 !left-0 lg:!left-auto !p-2 ',
                  list: '!py-1 [&>button]:text-center',
                }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    year: Number(e),
                  }))
                }
                errorMessage=""
              />
            </div>
          </div>
          <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
            This information was automatically generated from your
            injury/illness records. Please review for accuracy.
          </p>
        </div>
      </div>
      <p className="font-inter-variable text-neutral-tones-text/80 -tracking-text-area mt-15 text-lg leading-5 font-medium lg:hidden">
        This information was automatically generated from your injury/illness
        records. Please review for accuracy.
      </p>

      {!isLogsNotAvailable && (
        <div className="mt-8 flex w-full flex-col-reverse items-center justify-end gap-2.5 sm:flex-row-reverse lg:hidden">
          <div
            ref={refMob}
            className="relative flex w-full flex-1 justify-center"
          >
            <Button
              onClick={() => setToggleMob(!toggleMob)}
              otherClasses="!p-0 bg-transparent shadow-none !text-brand-byzantine !text-lg  hover:!bg-transparent hover:!border-transparent disabled:bg-transparent disabled:!text-neutral-tones-text/50 "
              disable={data?.cases?.length === 0}
            >
              <DownloadIcon className="rotate-180 [&>g>path]:fill-current" />{' '}
              Export
            </Button>
            {toggleMob && (
              <ul
                className={clsx(
                  'bg-light-translucent-background ring-neutral-tones-text/35 dark:bg-dark-translucent-background absolute top-10 left-2/4 z-10 flex max-w-100 min-w-100 -translate-x-2/4 flex-col rounded-md p-2.5 ring md:w-full lg:top-11',
                )}
              >
                {options.map((option, index) => (
                  <button
                    key={index}
                    className={clsx(
                      'font-inter-variable text-neutral-tones-text border-b-neutral-tones-text/35 hover:text-brand-byzantine global-transition cursor-pointer border-b py-2 text-left text-sm font-normal whitespace-nowrap first:pt-0 last:border-b-0 last:pb-0 disabled:cursor-not-allowed disabled:text-neutral-600',
                    )}
                    onClick={() => option.onClick()}
                  >
                    {option.label}
                  </button>
                ))}
              </ul>
            )}
          </div>
          <Button
            onClick={() => dispatch(toggleModal('osha-summary'))}
            otherClasses="flex-1 w-full"
            size={lg}
            disable={!data?.summary}
          >
            View Summary (OSHA 300A)
          </Button>
        </div>
      )}
      {!isLogsNotAvailable ? (
        <>
          <hr className="border-neutral-tones-text/50 mt-9 mb-6 lg:hidden" />
          <div className="flex items-stretch gap-2.5 lg:mt-12">
            <SearchInput
              name="search"
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  search: e?.target?.value,
                }))
              }
              placeholder="Enter reference ID, department, or name..."
              value={state?.search}
              disabled={false}
              errorMessage=""
              otherClasses={{ container: 'w-full ' }}
            />
            <Button
              onClick={() =>
                navigate(`/osha-reporting/resubmit?year=${state?.year}`)
              }
              otherClasses=" !py-0 h-full whitespace-nowrap !gap-0"
              size="smaller"
              disable={!isDateAvailable}
            >
              <RefreshIcon className="!mr-2.5 h-5 w-5 [&>g>path]:stroke-current" />
              Resubmit <span className="ml-1 hidden lg:block">To Osha</span>
            </Button>
          </div>
          <div className="global-scrollbar-style flex-1 overflow-auto px-0.5">
            <div className="flex w-full min-w-6xl flex-col">
              <div className="border-b-neutral-tones-text/50 relative mt-6 mb-12 flex flex-col gap-4 border-b pb-6 lg:flex-row lg:items-center lg:gap-9">
                <p className="font-inter-variable -tracking-form-input lg:-tracking-text-area text-brand-byzantine flex items-center gap-2.5 object-fill text-base leading-5 font-bold lg:text-lg">
                  <button
                    onClick={() => {
                      if (isFiltersActive) {
                        setState((prev) => ({
                          ...prev,
                          search: '',
                          department: '',
                          status: '',
                          classification: '',
                          typeOfLoss: '',
                          type: '',
                          limit: 10,
                        }));
                      }
                    }}
                    className="font-inter-variable -tracking-form-input lg:-tracking-text-area text-brand-byzantine flex cursor-pointer items-center gap-2.5 object-fill text-base leading-5 font-bold lg:text-lg"
                  >
                    {isFiltersActive ? (
                      <FilterClearIcon className="[&>path]:fill-brand-byzantine h-6 w-6 lg:h-9 lg:w-9" />
                    ) : (
                      <FilterIcon className="h-6 w-6 lg:h-9 lg:w-9" />
                    )}
                    Filters
                  </button>
                </p>
                <div className="flex flex-1 items-center gap-6">
                  <Select
                    filterSelect
                    list={Object.values(
                      OshaReportingControllerGetOshaSubmissionDetailsStatusEnum,
                    ).map((node) => ({
                      label: node,
                      value: node,
                    }))}
                    value={state?.status}
                    placeholder="Status"
                    otherClasses={{
                      container: 'w-full max-w-28',
                    }}
                    onSelect={(e) =>
                      setState((prev) => ({
                        ...prev,
                        status: e,
                      }))
                    }
                    errorMessage=""
                  />
                  <Select
                    filterSelect
                    list={departments}
                    value={state?.department}
                    placeholder="Departments"
                    otherClasses={{ container: 'w-full max-w-48' }}
                    onSelect={(e) =>
                      setState((prev) => ({
                        ...prev,
                        department: e,
                      }))
                    }
                    errorMessage=""
                  />

                  <Select
                    filterSelect
                    list={Object.values(
                      OshaReportingControllerGetOshaSubmissionDetailsClassificationEnum,
                    ).map((node) => ({
                      label: node,
                      value: node,
                    }))}
                    value={state?.classification}
                    placeholder="Classification"
                    otherClasses={{ container: 'w-full max-w-44' }}
                    onSelect={(e) =>
                      setState((prev) => ({
                        ...prev,
                        classification: e,
                      }))
                    }
                    errorMessage=""
                  />
                  <Select
                    filterSelect
                    list={Object.values(
                      OshaReportingControllerGetOshaSubmissionDetailsTypeOfLossEnum,
                    ).map((node) => ({
                      label: node,
                      value: node,
                    }))}
                    value={state?.typeOfLoss}
                    placeholder="Type Of Loss"
                    otherClasses={{ container: 'w-full max-w-56' }}
                    onSelect={(e) =>
                      setState((prev) => ({
                        ...prev,
                        typeOfLoss: e,
                      }))
                    }
                    errorMessage=""
                  />
                  <Select
                    filterSelect
                    list={Object.values(
                      OshaReportingControllerGetOshaSubmissionDetailsTypeEnum,
                    ).map((node) => ({
                      label: node,
                      value: node,
                    }))}
                    value={state?.type}
                    placeholder="Type"
                    otherClasses={{ container: 'w-full max-w-24' }}
                    onSelect={(e) =>
                      setState((prev) => ({
                        ...prev,
                        type: e,
                      }))
                    }
                    errorMessage=""
                  />
                </div>
              </div>
            </div>
            {isIncidentsLoading ? (
              <div>
                <Loader
                  variant="compact"
                  otherClasses="bg-neutral-tones-background/50 z-20 backdrop-blur-[2px] lg:!w-[calc(100%-310px)] lg:right-0 lg:left-auto !fixed !top-0 left-0 h-dvh"
                />
              </div>
            ) : (
              <div className="w-full min-w-6xl">
                <div className="border-neutral-tones-text/50 grid grid-cols-12 gap-4 border-b pb-6">
                  <h6 className="text-neutral-tones-text/50 font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                    Sr.
                  </h6>
                  <h6 className="text-neutral-tones-text/50 font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                    Ref#
                  </h6>
                  <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                    Status
                  </h6>
                  <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                    Employee Effected
                  </h6>
                  <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                    Date
                  </h6>
                  <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                    Type Of Loss
                  </h6>
                  <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                    Failed Reason
                  </h6>
                  <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                    Actions
                  </h6>
                </div>

                {data?.cases?.length > 0 ? (
                  <>
                    {data?.cases?.map((incident: any, index) => {
                      const errors: any = incident?.failedReason?.error;

                      return (
                        <div
                          key={index}
                          className="border-b-neutral-tones-text/50 grid grid-cols-12 items-center gap-4 border-b py-5"
                        >
                          <p className="text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input col-span-1 text-sm leading-4 font-bold lg:text-base lg:leading-5 lg:font-normal">
                            {(state.page - 1) * state.limit + index + 1}
                          </p>
                          <p className="text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input col-span-2 text-sm leading-4 font-normal uppercase first-letter:uppercase lg:text-base lg:leading-5">
                            {incident.investigation?.incident?.incidentRefId}
                          </p>
                          <p
                            className={clsx(
                              'font-inter-variable lg:-tracking-form-input col-span-1 text-sm leading-4 font-bold lowercase first-letter:uppercase lg:text-base lg:leading-5',
                              incident.status ===
                                OshaReportingControllerGetOshaSubmissionDetailsStatusEnum.Done
                                ? 'text-brand-papery'
                                : incident.status ===
                                    OshaReportingControllerGetOshaSubmissionDetailsStatusEnum.Failed
                                  ? 'text-brand-posh-pink'
                                  : incident.status ===
                                      OshaReportingControllerGetOshaSubmissionDetailsStatusEnum.Pending &&
                                    'text-brand-fair-orange',
                            )}
                          >
                            {incident.status}
                          </p>
                          <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 text-sm leading-4 font-normal lg:text-base lg:leading-5">
                            {incident?.investigation?.incident?.user?.fullName}
                          </p>
                          <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-1 text-sm leading-4 font-normal lg:text-base lg:leading-5">
                            {moment(
                              incident?.investigation?.incident?.incidentDate
                                ? incident?.investigation?.incident
                                    ?.incidentDate
                                : incident?.investigation?.incident?.createdAt,
                            ).format('YYYY-MM-DD')}
                          </p>
                          <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 text-sm leading-4 font-normal lg:text-base lg:leading-5">
                            {incident?.investigation?.typeOfLoss}
                          </p>
                          <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 text-sm leading-4 font-normal lg:text-base lg:leading-5">
                            {errors?.map((node: string) => node) || '---'}
                          </p>
                          <div className="col-span-1">
                            <Button
                              // size="smaller"
                              otherClasses="w-10 h-10 !p-0"
                              onClick={() => {
                                const baseUrl = `/injuries/${incident?.investigation?.id}`;
                                const url =
                                  incident?.status ===
                                  OshaReportingControllerGetOshaSubmissionDetailsStatusEnum.Failed
                                    ? `${baseUrl}?onDemandEdit=true`
                                    : baseUrl;

                                window.open(url, '_blank');
                              }}
                            >
                              <OutlineEyeIcon className="h-6 w-6 [&>path]:fill-current" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <NoItemsFound message={noDataMessage} />
                )}
              </div>
            )}
          </div>
          {data?.cases?.length > 0 && (
            <Pagination
              currentPage={state.page}
              totalPages={data?.meta?.totalPages}
              totalItems={data?.meta?.total}
              limit={data?.meta?.limit}
              showingItems={data?.cases?.length}
              onPageChange={handlePageChange}
              onLimitChange={(e) =>
                setState((prev) => ({ ...prev, limit: e, page: 1 }))
              }
              otherClasses=""
            />
          )}
        </>
      ) : (
        <OshaSummaryModal
          isLogsNotAvailable={isLogsNotAvailable}
          year={state?.year}
          data={data?.summary}
        />
      )}

      {modalName === 'osha-summary' && (
        <ModalComponent
          otherClasses="px-4 lg:px-0 py-6 "
          additionalClass={clsx(
            'p-5 lg:p-6 rounded-md h-fit max-h-full max-w-3xl w-full global-scrollbar-style',
          )}
          customClose={() => {
            dispatch(toggleModal(''));
          }}
        >
          <OshaSummaryModal
            handleClose={() => dispatch(toggleModal(''))}
            data={data?.summary}
          />
        </ModalComponent>
      )}
    </div>
  );
};

export default OshaReportingLogs;
